import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { Common } from "@/components/common";
import { Icon } from "@/components/Icons";
import { Table, Td, Th, CustomScrollbar, ModalContent } from "@/components";
import { SnackbarUtils } from "@/utils";
import axios from "axios";
import {
  useAssignCustomer,
  useToggleCustomerAssignment,
  useRemoveCustomerAssignment,
} from "../services/exclusiveLists";

const tableHeaderClasses = "bg-neutral-20 font-bold";
const tableCellClasses =
  "!whitespace-normal border-b border-neutral-20 px-2 py-1";
const stickyBottomCellClasses = `${tableCellClasses} bg-neutral-5 sticky bottom-0`;

interface AssignedCustomer {
  accountId: string;
  tradingName: string;
  customerCode: string;
  isActive: boolean;
  status: string;
  address: string;
  email: string;
  assignedExclusiveLists: string;
  assignmentId: string;
}

interface CustomerSearchResult {
  accountId: string;
  tradingName: string;
  customerCode: string;
  status: string;
  address: string;
  email: string;
}

// Pending toggle: track which assignmentIds have been toggled and their new value
interface PendingToggle {
  assignmentId: string;
  isActive: boolean;
}

// Pending new assignment (not yet saved)
interface PendingAssignment {
  customer: CustomerSearchResult;
}

const AssignCustomersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id") || "";

  const [listName, setListName] = useState("");
  const [customers, setCustomers] = useState<AssignedCustomer[]>([]);
  const [loading, setLoading] = useState(false);

  // Pending changes (not yet saved)
  const [pendingToggles, setPendingToggles] = useState<PendingToggle[]>([]);
  const [pendingAssignments, setPendingAssignments] = useState<
    PendingAssignment[]
  >([]);

  const hasPendingChanges =
    pendingToggles.length > 0 || pendingAssignments.length > 0;

  // Search combobox state
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState<CustomerSearchResult[]>(
    []
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [comboboxKey, setComboboxKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<{
    assignmentId: string;
    name: string;
  } | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assignMuta = useAssignCustomer();
  const toggleMuta = useToggleCustomerAssignment();
  const removeMuta = useRemoveCustomerAssignment();

  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [detailRes, customersRes] = await Promise.all([
        axios.get("exclusivelists/detail", { params: { id } }),
        axios.get("exclusivelists/GetAssignedCustomers", {
          params: { exclusiveListId: id },
        }),
      ]);
      setListName(detailRes.data?.data?.name || "");
      setCustomers(customersRes.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchKeyword.length < 2) {
      setSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get("exclusivelists/SearchCustomers", {
          params: { keywords: searchKeyword, exclusiveListId: id },
        });
        setSearchResults(res.data?.data || []);
      } finally {
        setSearchLoading(false);
      }
    }, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword]);

  // Stage a new assignment (don't save yet)
  const handleStageAssign = (customer: CustomerSearchResult) => {
    const alreadyAssigned = customers.some(
      (c) => c.accountId === customer.accountId
    );
    const alreadyPending = pendingAssignments.some(
      (p) => p.customer.accountId === customer.accountId
    );
    if (alreadyAssigned || alreadyPending) return;
    setPendingAssignments((prev) => [...prev, { customer }]);
    setSearchKeyword("");
    setSearchResults([]);
    setComboboxKey((k) => k + 1);
  };

  const handleRemovePendingAssignment = (accountId: string) => {
    setPendingAssignments((prev) =>
      prev.filter((p) => p.customer.accountId !== accountId)
    );
  };

  // Stage a toggle (don't save yet)
  const handleStageToggle = (
    assignmentId: string,
    currentIsActive: boolean
  ) => {
    const newValue = !currentIsActive;
    setPendingToggles((prev) => {
      const existing = prev.find((t) => t.assignmentId === assignmentId);
      if (existing) {
        // If toggling back to original, remove the pending toggle
        const original = customers.find((c) => c.assignmentId === assignmentId);
        if (original && original.isActive === newValue) {
          return prev.filter((t) => t.assignmentId !== assignmentId);
        }
        return prev.map((t) =>
          t.assignmentId === assignmentId ? { ...t, isActive: newValue } : t
        );
      }
      return [...prev, { assignmentId, isActive: newValue }];
    });
  };

  // Get the effective isActive for a row (considering pending toggles)
  const getEffectiveIsActive = (customer: AssignedCustomer) => {
    const pending = pendingToggles.find(
      (t) => t.assignmentId === customer.assignmentId
    );
    return pending !== undefined ? pending.isActive : customer.isActive;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save new assignments
      for (const pa of pendingAssignments) {
        const res = await assignMuta.mutateAsync({
          exclusiveListId: id,
          customerAccountId: pa.customer.accountId,
        });
        if (!res?.data?.success) {
          SnackbarUtils.error(`Failed to assign "${pa.customer.tradingName}".`);
          return;
        }
      }
      // Save pending toggles
      for (const pt of pendingToggles) {
        await toggleMuta.mutateAsync({
          assignmentId: pt.assignmentId,
          isActive: pt.isActive,
        });
      }
      SnackbarUtils.success("Changes saved.");
      setPendingAssignments([]);
      setPendingToggles([]);
      loadData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingAssignments([]);
    setPendingToggles([]);
  };

  const handleDeleteConfirm = (assignmentId: string, name: string) => {
    setConfirmDelete({ assignmentId, name });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const res = await removeMuta.mutateAsync(confirmDelete.assignmentId);
    if (res?.data?.success) {
      SnackbarUtils.success("Customer assignment removed.");
      setConfirmDelete(null);
      loadData();
    }
  };

  const assignedAndPendingIds = [
    ...customers.map((c) => c.accountId),
    ...pendingAssignments.map((p) => p.customer.accountId),
  ];

  return (
    <div className="flex flex-col p-4 bg-white rounded shadow h-full">
      {/* Header */}
      <div className="flex items-center pb-4 mb-4 shadow-underline">
        <button className="mr-2" onClick={() => navigate(-1)} aria-label="Back">
          <Icon.Back iconType="fill" className="fill-neutral-90" />
        </button>
        <h5 className="text-hd5 font-medium">
          {loading ? "..." : `Assign customers — ${listName}`}
        </h5>
      </div>

      {/* Table */}
      <div className="flex flex-col flex-1 min-h-0">
        <CustomScrollbar>
          <Table className="relative w-full border border-line border-separate border-spacing-0">
            <thead>
              <tr>
                <Th className={`${tableHeaderClasses} w-[5%]`}>Code</Th>
                <Th className={tableHeaderClasses}>Name</Th>
                <Th className={`${tableHeaderClasses} w-[10%]`}>Status</Th>
                <Th className={`${tableHeaderClasses} w-[22%]`}>Address</Th>
                <Th className={`${tableHeaderClasses} w-[16%]`}>Email</Th>
                <Th className={`${tableHeaderClasses} w-[14%]`}>
                  Assigned exclusive lists
                </Th>
                <Th className={`${tableHeaderClasses} w-[8%] text-center`}>
                  Active
                </Th>
                <Th className={`${tableHeaderClasses} w-[5%]`}></Th>
              </tr>
            </thead>
            <tbody>
              {/* Loading */}
              {loading && (
                <tr>
                  <Td colSpan={8}>
                    <Icon.Loading size={60} className="mx-auto my-36" />
                  </Td>
                </tr>
              )}

              {/* Saved rows */}
              {!loading &&
                customers.map((c) => {
                  const effectiveIsActive = getEffectiveIsActive(c);
                  const isToggled = pendingToggles.some(
                    (t) => t.assignmentId === c.assignmentId
                  );
                  return (
                    <tr
                      key={c.accountId}
                      className={clsx(
                        "hover:bg-neutral-5",
                        isToggled && "bg-yellow-50"
                      )}
                    >
                      <Td className={`${tableCellClasses} text-neutral-60`}>
                        {c.customerCode}
                      </Td>
                      <Td className={`${tableCellClasses} font-medium`}>
                        {c.tradingName}
                      </Td>
                      <Td className={tableCellClasses}>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            effectiveIsActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {effectiveIsActive ? "Active" : "Inactive"}
                        </span>
                      </Td>
                      <Td className={tableCellClasses}>{c.address}</Td>
                      <Td className={tableCellClasses}>{c.email}</Td>
                      <Td className={tableCellClasses}>
                        {c.assignedExclusiveLists}
                      </Td>
                      <Td className={`${tableCellClasses} text-center`}>
                        <div className="flex justify-center">
                          <Common.Checkbox
                            id={`toggle-${c.assignmentId}`}
                            ipSize="md"
                            checked={effectiveIsActive}
                            onChange={() =>
                              handleStageToggle(
                                c.assignmentId,
                                effectiveIsActive
                              )
                            }
                          />
                        </div>
                      </Td>
                      <Td className={tableCellClasses}>
                        <div className="flex justify-center">
                          <Common.Button
                            variant="outline"
                            color="red"
                            size="md"
                            iconType="fill"
                            iconLeft={Icon.Trash}
                            outline
                            className="bg-transparent border-transparent hover:!border-transparent"
                            onClick={() =>
                              handleDeleteConfirm(c.assignmentId, c.tradingName)
                            }
                            isLoading={removeMuta.isLoading}
                          />
                        </div>
                      </Td>
                    </tr>
                  );
                })}

              {/* Pending (unsaved) new assignments */}
              {!loading &&
                pendingAssignments.map((pa) => (
                  <tr key={pa.customer.accountId} className="bg-blue-10">
                    <Td className={`${tableCellClasses} text-neutral-60`}>
                      {pa.customer.customerCode}
                    </Td>
                    <Td className={`${tableCellClasses} font-medium`}>
                      <div className="flex items-center gap-1">
                        {pa.customer.tradingName}
                      </div>
                    </Td>
                    <Td className={tableCellClasses}>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        Active
                      </span>
                    </Td>
                    <Td className={tableCellClasses}>{pa.customer.address}</Td>
                    <Td className={tableCellClasses}>{pa.customer.email}</Td>
                    <Td className={tableCellClasses}></Td>
                    <Td className={`${tableCellClasses} text-center`}>
                      <div className="flex justify-center">
                        <Common.Checkbox
                          id={`pending-toggle-${pa.customer.accountId}`}
                          ipSize="md"
                          checked
                          onChange={() => {}}
                          disabled
                        />
                      </div>
                    </Td>
                    <Td className={tableCellClasses}>
                      <div className="flex justify-center">
                        <Common.Button
                          variant="outline"
                          color="red"
                          size="md"
                          iconType="fill"
                          iconLeft={Icon.Trash}
                          outline
                          className="bg-transparent border-transparent hover:!border-transparent"
                          onClick={() =>
                            handleRemovePendingAssignment(pa.customer.accountId)
                          }
                        />
                      </div>
                    </Td>
                  </tr>
                ))}

              {/* Sticky bottom row — search to assign */}
              {!loading && (
                <tr>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}>
                    <Common.CustomCombobox
                      key={comboboxKey}
                      data={searchResults as any}
                      isLoading={searchLoading}
                      value={searchKeyword}
                      placeholder="Search by name or code..."
                      keySearch="tradingName"
                      optionsHeader={() => (
                        <div className="w-full flex flex-row px-3 py-2 select-none shadow-overline">
                          <div className="basis-1/3 font-bold">CODE</div>
                          <div className="basis-2/3 font-bold">NAME</div>
                        </div>
                      )}
                      renderItem={({ item }: any) => (
                        <div
                          className={clsx(
                            "w-full flex flex-row items-center cursor-pointer px-3 py-4 select-none shadow-overline",
                            "hover:bg-neutral-5",
                            assignedAndPendingIds.includes(item.accountId)
                              ? "bg-blue-10"
                              : ""
                          )}
                        >
                          <div className="basis-1/3 text-neutral-60">
                            {item.customerCode}
                          </div>
                          <div className="basis-2/3">{item.tradingName}</div>
                        </div>
                      )}
                      onSelect={(selected: any) => handleStageAssign(selected)}
                      onChangeInput={(input: string | undefined) =>
                        setSearchKeyword(input ?? "")
                      }
                      optionsClassName="min-w-[420px]"
                      noDataDesc={
                        searchKeyword.length < 2
                          ? "Type 2 more characters to search customers"
                          : "No customers found"
                      }
                    />
                  </Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                  <Td className={stickyBottomCellClasses}></Td>
                </tr>
              )}
            </tbody>
          </Table>
        </CustomScrollbar>

        {/* Save / Cancel bar — only visible when there are pending changes */}
        {hasPendingChanges && (
          <div className="pt-2 flex items-center gap-2">
            <Common.Button
              className="ml-auto"
              color="transparent"
              size="lg"
              outline
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="blue"
              size="lg"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save changes
            </Common.Button>
          </div>
        )}
      </div>

      {/* Confirm remove assignment modal */}
      <ModalContent
        open={!!confirmDelete}
        onOverlayClick={() => setConfirmDelete(null)}
        panelClassWidth="sm:max-w-sm sm:w-full"
      >
        <div className="p-6">
          <h3 className="text-hd5 font-semibold mb-2">Remove customer</h3>
          <p className="text-neutral-60 mb-6">
            Are you sure you want to remove &quot;{confirmDelete?.name}&quot;
            from this exclusive list?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Common.Button
              color="transparent"
              size="lg"
              outline
              onClick={() => setConfirmDelete(null)}
              disabled={removeMuta.isLoading}
            >
              Cancel
            </Common.Button>
            <Common.Button
              color="red"
              size="lg"
              onClick={handleConfirmDelete}
              isLoading={removeMuta.isLoading}
            >
              Remove
            </Common.Button>
          </div>
        </div>
      </ModalContent>
    </div>
  );
};

export default AssignCustomersPage;
